import classNames from 'classnames';
import * as df from 'date-fns';
import { camelCase } from 'lodash-es';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { PaymentMethod } from '../../../../../interfaces/merchants/resources/books';
import BooksLabel from '../common/labels/BooksLabel';
import GoogleCalendarTemplateUrl from '../common/texts/GoogleCalendarTemplateUrl';
import BooksMenuMerchant from '../BooksMenuMerchant';

import css from './BooksSchoolItemList.module.scss';

interface Props {
  className?: string;
  nickname?: string;
  resourceName?: string;
  resourceId?: string;
  resourceImage?: string;
  resourceUrl?: string;
  payment?: PaymentMethod['method'];
  date: string;
  startTime: string;
  endTime: string;
  lessonName: string;
  price?: string;
  instructors?: string[];
  googleCalendarTemplateUrl?: string;
}

const BooksSchoolItemList = ({
  className,
  date,
  startTime,
  endTime,
  lessonName,
  price,
  payment,
  instructors,
  googleCalendarTemplateUrl,
  ...props
}: Props) => {
  const classes = classNames(css.BooksSchoolItemList, className);
  const intl = useIntl();

  return (
    <div className={classes}>
      <BooksMenuMerchant
        image={props.resourceImage}
        name={props.resourceName}
        nickname={props.nickname}
        url={props.resourceUrl}
      />
      <dl className={css.BooksSchoolItemList__List}>
        <dt className={css.BooksSchoolItemList__ListTitle}>
          {intl.formatMessage({ id: 'books.booksMenu.choiceMenu' })}
        </dt>
        <dd className={css.BooksSchoolItemList__ListData} data-style="name">
          {lessonName}
        </dd>
        <dt className={css.BooksSchoolItemList__ListTitle}>
          {intl.formatMessage({ id: 'books.booksMenu.bookDate' })}
        </dt>
        <dd className={css.BooksSchoolItemList__ListData} data-style="date">
          <time className={css.BooksSchoolItemList__ListDate}>
            {intl.formatDate(df.format(date), {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'short',
            })}
            <br />
            {startTime}
            <span> - </span>
            {endTime}
          </time>
          {googleCalendarTemplateUrl && (
            <GoogleCalendarTemplateUrl
              googleCalendarTemplateUrl={googleCalendarTemplateUrl}
            />
          )}
          {instructors && instructors.length > 0 && (
            <span className={css.BooksSchoolItemList__Instructor}>
              {intl.formatMessage({ id: 'books.forms.staff' })}:
              {instructors.map((instructor, i) => (
                <span
                  className={css.BooksSchoolItemList__InstructorName}
                  key={i}
                >
                  {instructor}
                </span>
              ))}
            </span>
          )}
        </dd>
        {price && parseFloat(price) > 0 && (
          <>
            <dt className={css.BooksSchoolItemList__ListTitle}>
              {intl.formatMessage({ id: 'books.booksMenu.totalPrice' })}
            </dt>
            <dd
              className={css.BooksSchoolItemList__ListData}
              data-style="price"
            >
              {price}円
            </dd>
            {payment && payment !== 'unspecified' && (
              <>
                <dt className={css.BooksSchoolItemList__ListTitle}>
                  {intl.formatMessage({
                    id: 'books.booksMenu.paymentMethod',
                  })}
                </dt>
                <dd className={css.BooksSchoolItemList__ListData}>
                  {payment === 'credit_card' && (
                    <span className={css.BooksSchoolItemList__ListTag}>
                      <BooksLabel
                        label={intl.formatMessage({
                          id: 'books.paymentMethod.prepayment',
                        })}
                      />
                    </span>
                  )}
                  {intl.formatMessage({
                    id: `books.paymentMethod.${camelCase(payment)}`,
                  })}
                </dd>
              </>
            )}
          </>
        )}
      </dl>
    </div>
  );
};

export default BooksSchoolItemList;
